/**
 * `RoleRoute` — guardia di rotta difensiva basata sul `Role` applicativo.
 *
 * Design ref: "Routing e guardie per ruolo" — `RoleRoute allowed={[...]}` nega
 * l'accesso alle pagine admin ai non‑admin (4.5). Questa è una guardia
 * DIFENSIVA lato client: migliora la UX evitando di mostrare pagine per cui
 * l'utente non è autorizzato, ma NON è la fonte autorevole dell'autorizzazione.
 *
 * L'autorizzazione autorevole resta l'API, che nega qualsiasi accesso admin con
 * HTTP `403` a prescindere dal `Role` dello User; il client tratta il `403`
 * come accesso negato (vedi `api/client.ts`). *Validates: 4.5.*
 *
 * Comportamento:
 *  - richiede una sessione autenticata (come `ProtectedRoute`);
 *  - se il `role` corrente non è tra quelli in `allowed`, mostra la pagina di
 *    "accesso negato" (o reindirizza, se `redirectTo` è fornito);
 *  - il ruolo `null` (non ancora risolto o assente) non è mai consentito.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Role } from '../shared/dto';
import { useAuth } from '../auth/AuthProvider';

interface RoleRouteProps {
  /** Ruoli ammessi ad accedere alla rotta. */
  allowed: readonly Role[];
  /** Contenuto protetto. Se assente, usa `<Outlet />` (rotte annidate). */
  children?: ReactNode;
  /** Percorso di redirect per utenti non autenticati. */
  redirectTo?: string;
  /**
   * Percorso di redirect per utenti autenticati ma senza ruolo consentito.
   * Se omesso, viene mostrata la pagina di "accesso negato" in-place.
   */
  deniedRedirectTo?: string;
  /** Fallback mostrato durante il caricamento della sessione. */
  fallback?: ReactNode;
}

/** Pagina di accesso negato mostrata dalla guardia difensiva. */
export function AccessDenied() {
  return (
    <section role="alert" aria-labelledby="access-denied-title">
      <h1 id="access-denied-title">Accesso negato</h1>
      <p>Non hai i permessi necessari per visualizzare questa pagina.</p>
    </section>
  );
}

export function RoleRoute({
  allowed,
  children,
  redirectTo = '/login',
  deniedRedirectTo,
  fallback = <p role="status">Caricamento della sessione…</p>,
}: RoleRouteProps) {
  const { isLoading, isProfileLoading, isAuthenticated, role } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // Utente autenticato ma con ruolo autorevole ancora in caricamento
  // (`/api/me` non ha ancora risposto e nessun ruolo dai claim OIDC): trattiamo
  // come "in caricamento" per non mostrare "Accesso negato" a un admin il cui
  // ruolo vive solo in Mongo. Solo quando il ruolo è noto valutiamo l'accesso.
  if (role === null && isProfileLoading) {
    return <>{fallback}</>;
  }

  const isAllowed = role !== null && allowed.includes(role);
  if (!isAllowed) {
    if (deniedRedirectTo) {
      return <Navigate to={deniedRedirectTo} replace />;
    }
    return <AccessDenied />;
  }

  return <>{children ?? <Outlet />}</>;
}
